import React, { useState, useEffect, useRef } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";
import LoadingIcons from "react-loading-icons";
import { searchUsers } from "../utils/Api";

interface AddMemberModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  newMemberEmail: string;
  setNewMemberEmail: (email: string) => void;
  newMemberRole: string;
  setNewMemberRole: (role: string) => void;
  addMemberError: string | null;
  addMemberLoading: boolean;
  onAddMember: () => void;
}

interface UserSuggestion {
  email: string;
  name: string;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  setIsOpen,
  newMemberEmail,
  setNewMemberEmail,
  newMemberRole,
  setNewMemberRole,
  addMemberError,
  addMemberLoading,
  onAddMember,
}) => {
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  useEffect(() => {
    if (!isOpen) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [isOpen]);

  const handleEmailChange = (email: string) => {
    setNewMemberEmail(email);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (email.length >= 2) {
      setSearchLoading(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await searchUsers(email);
          setSuggestions(response.data.users || []);
          setShowSuggestions(true);
        } catch (error) {
          console.error("Failed to search users:", error);
          setSuggestions([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setSearchLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion: UserSuggestion) => {
    setNewMemberEmail(suggestion.email);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="w-full">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
          Add member
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          Invite a user to join this organisation.
        </p>

        {addMemberError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {addMemberError}
          </p>
        )}

        <div className="space-y-4">
          <div className="relative">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email address
            </label>
            <input
              ref={inputRef}
              type="email"
              id="email"
              value={newMemberEmail}
              onChange={(e) => handleEmailChange(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="member@example.com"
              disabled={addMemberLoading}
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg max-h-48 overflow-y-auto"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <div className="text-sm font-medium text-gray-900">{suggestion.name}</div>
                    <div className="text-xs text-gray-500">{suggestion.email}</div>
                  </button>
                ))}
              </div>
            )}
            {searchLoading && (
              <div className="absolute right-3 top-[38px] flex items-center">
                <LoadingIcons.TailSpin stroke="#9CA3AF" width="16" height="16" />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1.5">
              Role
            </label>
            <select
              id="role"
              value={newMemberRole}
              onChange={(e) => setNewMemberRole(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              disabled={addMemberLoading}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex gap-2 pt-2">
            <SecondaryButton
              type="button"
              onClick={() => setIsOpen(false)}
              disabled={addMemberLoading}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={onAddMember}
              disabled={addMemberLoading}
              className="flex-1"
            >
              {addMemberLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingIcons.TailSpin stroke="#fff" width="16" height="16" />
                  Adding...
                </span>
              ) : (
                "Add member"
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddMemberModal;
